import * as amplitude from '@amplitude/analytics-browser'
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'
import { Config } from '@app/src/config'
import type { FC, ReactNode } from 'react'
import React, { useEffect } from 'react'
import { useAccount } from './AccountContext'
import type { TrackEvent } from './AmplitudeContext'
import AmplitudeContext from './AmplitudeContext'
import { useAuthentication } from './AuthenticationContext'

type AmplitudeContextProviderProps = {
  children: ReactNode
}

const AmplitudeContextProvider: FC<AmplitudeContextProviderProps> = ({ children }: AmplitudeContextProviderProps) => {
  const { account } = useAccount()
  const { scope } = useAuthentication()

  useEffect(() => {
    if (Config.AMPLITUDE_API_KEY) {
      amplitude.init(Config.AMPLITUDE_API_KEY, {
        defaultTracking: true,
        minIdLength: 1,
        serverZone: 'EU',
      })
      amplitude.add(engagementPlugin())
    }
  }, [Config.AMPLITUDE_API_KEY])

  useEffect(() => {
    if (!account || !Config.AMPLITUDE_API_KEY) return

    amplitude.setUserId(account.user.id.toString())

    const identify = new amplitude.Identify()
    identify.set('user_id', account.user.id)
    if (account.organization?.id != null) identify.set('organization_id', account.organization.id)
    if (account.organization?.name != null) identify.set('organization_name', account.organization.name)
    if (scope.solution != null) identify.set('solution_type', scope.solution)
    if (scope.role != null) identify.set('user_role', scope.role)
    identify.set('is_owner_restricted', account.isOwnerRestricted)
    identify.set('worldfavor_user', account.user?.email?.endsWith('@worldfavor.com') ?? false)

    amplitude.identify(identify)
  }, [account, scope])

  const trackEvent = ({ name, eventProps, eventOptions }: TrackEvent) => {
    amplitude.track(name, eventProps, eventOptions)
  }

  /** Used to track leaving-events when the user closes the browser/tab
   * When the browser is closing, we can't send requests with fetch, but we can send beacons */
  const trackEventCloseBrowser = (event: TrackEvent) => {
    amplitude.setTransport('beacon')
    trackEvent(event)
    amplitude.flush()
  }

  return (
    <AmplitudeContext.Provider value={{ trackEvent, trackEventCloseBrowser }}>{children}</AmplitudeContext.Provider>
  )
}

export default AmplitudeContextProvider
